import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";

import { WizardOverlay, useWizard } from "Wizard";
import { ACCENT_COLOR } from "Wizard/theme";
import SessionPicker from "Wizard/SessionPicker";

export default function Container() {
  const { isOpen, modalOpen, collapsed, expandOverlay, committedProgramName, data } = useWizard();
  const programName = committedProgramName || data.generalInfo.programName || "Program";

  return (
    <>
      <Dashboard>
        <SessionPicker />
      </Dashboard>
      <AnimatePresence>
        {isOpen && (
          <WizardBox key="wizard-box" $collapsed={collapsed}>
            <Backdrop
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: collapsed ? 0 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              $collapsed={collapsed}
            />
            <CardOuter $collapsed={collapsed}>
              <PeekHandle
                onClick={expandOverlay}
                $collapsed={collapsed}
              >
                <PeekArrow className="fas fa-chevron-left" />
                <PeekTitle>{programName}</PeekTitle>
                <PeekArrow className="fas fa-chevron-left" />
              </PeekHandle>
              <CardWrapper
                as={motion.div}
                initial={{ opacity: 0, scale: 0.92, rotateX: -14, y: 40 }}
                animate={{ opacity: 1, scale: 1, rotateX: 0, y: 0 }}
                exit={{ opacity: 0, scale: 0.85, rotateX: 0, y: 0 }}
                style={{ transformOrigin: "bottom center" }}
                transition={{
                  type: "spring",
                  stiffness: 150,
                  damping: 15,
                  mass: 1,
                }}
              >
                <Inner $modalOpen={modalOpen}>
                  <WizardOverlay />
                </Inner>
              </CardWrapper>
            </CardOuter>
          </WizardBox>
        )}
      </AnimatePresence>
    </>
  );
}

const Dashboard = styled.div`
  position: absolute;
  top: 0;
  left: 340px;
  right: 0;
  bottom: 0;
`;

const WizardBox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1200px;
  pointer-events: ${(p) => (p.$collapsed ? "none" : "auto")};
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  pointer-events: ${(p) => (p.$collapsed ? "none" : "auto")};
`;

const CardOuter = styled.div`
  position: relative;
  z-index: 1;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${(p) =>
    p.$collapsed ? "translateX(calc(50vw + 50% - 36px))" : "translateX(0)"};
`;

const PeekHandle = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 36px;
  z-index: 10;
  appearance: none;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  cursor: pointer;
  pointer-events: ${(p) => (p.$collapsed ? "auto" : "none")};
  opacity: ${(p) => (p.$collapsed ? 1 : 0)};
  transition: opacity 0.3s 0.15s, background 0.2s;
  border-radius: 32px 0 0 32px;
  padding: 24px 0;

  &:hover {
    background: rgba(1, 96, 153, 0.06);
  }

  &:active {
    background: rgba(1, 96, 153, 0.12);
  }
`;

const PeekArrow = styled.i`
  font-size: 11px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.2s;

  ${PeekHandle}:hover & {
    color: ${ACCENT_COLOR};
  }

  ${PeekHandle}:active & {
    color: #014a75;
  }
`;

const PeekTitle = styled.span`
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 260px;
  letter-spacing: 0.02em;
  transition: color 0.2s;

  ${PeekHandle}:hover & {
    color: ${ACCENT_COLOR};
  }

  ${PeekHandle}:active & {
    color: #014a75;
  }
`;

const CardWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const Inner = styled.div`
  height: 90vh;
  width: 90vw;
  max-width: 980px;
  max-height: 920px;
  border-radius: 32px;
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.4) 1px 2px 8px;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, filter 0.3s ease;
  ${(p) => p.$modalOpen && `
    transform: scale(0.98);
    filter: blur(2px);
  `}
`;
